import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { AppState } from '../core/store'
import {
  deleteUserPreference,
  fetchClientViewTracking,
  fetchMyViewTracking,
  fetchTeamViewTracking,
  fetchTracking,
  fetchTrackingDetails,
  filtersChanged,
  filtersChangedMyView,
  filtersChangedTeamView,
  inceptionDateChanged,
  saveTrackingPreferences,
  setSelectedUserPreferences,
  updateColumnValue,
  updateComment,
  updateCompleteProgram,
  updateUserPreference,
  updateUserValue,
} from './store/tracking.actions'

import { firstValueFrom, Observable, take } from 'rxjs'
import { DataCounts, TrackingData, TrackingSelectors, TrackingViewsData } from './tracking.model'
import {
  selectInceptionTimeInterval,
  selectIsDetailsLoading,
  selectIsLoading,
  selectMyViewTrackingSelectors,
  selectTeamViewTrackingSelectors,
  selectTrackingClientViewsData,
  selectTrackingData,
  selectTrackingMyViewsData,
  selectTrackingSelectors,
  selectTrackingTeamViewsData,
  selectTrackingUserPreferences,
} from './store/tracking.selectors'
import { BasicControl } from '../management-information/model/management-information.model'
import { NewPreferenceDialogService } from './tracking-new-preference-dialog.service'
import { SelectedUserPreferences } from '../core/model/reinsurer.model'
import { ThemeContainerComponent } from '../shared/theme.container'
import { TrackingComponent } from './tracking-component/tracking.component'
import { AsyncPipe } from '@angular/common'
import { ApiResponse } from '../api/model/api.model'
import { User } from '../api/model/backend.model'
import { BackendService } from '../api/backend/backend.service'
import { fi } from 'date-fns/locale'
import { MatSnackBar } from '@angular/material/snack-bar'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-tracking-container',
  template: `<app-theme-container
    ><app-tracking
      [trackingData]="trackingData$ | async"
      [trackingMyViewsData]="trackingMyViewsData$ | async"
      [trackingTeamViewsData]="trackingTeamViewsData$ | async"
      [trackingClientViewsData]="trackingClientViewsData$ | async"
      [selectors]="selectors$ | async"
      [myViewSelectors]="myViewSelectors$ | async"
      [teamViewSelectors]="teamViewSelectors$ | async"
      [inceptionInterval]="inceptionInterval$ | async"
      [isLoading]="isLoading$ | async"
      [isDetailsLoading]="isDetailsLoading$ | async"
      [userPreferences]="userPreferences$ | async"
      [usersResponse]="users$ | async"
      (filtersChanged)="this.onFiltersChanged($event)"
      (filtersMyViewChanged)="this.onFiltersChangeMyView($event)"
      (filtersTeamViewChanged)="this.onFiltersChangeTeamView($event)"
      (fetchTrackingDetails)="this.onFetchTrackingDetailsData($event)"
      (inceptionDateChanged)="this.onInceptionDateChanged($event)"
      (updateComplete)="this.onUpdateComplete($event)"
      (updateUserValue)="this.onUpdateUserValue($event)"
      (updateColumnValue)="this.onUpdateColumnValue($event)"
      (updateComment)="this.onUpdateComment($event)"
      (saveTrackingPreferences)="this.onSavePreference($event)"
      (setTrackingPreferences)="this.setTrackingPreferences($event)"
      (deleteTrackingPreferences)="this.deleteTrackingPreferences($event)"
      (updateTrackingPreferences)="this.updateTrackingPreferences($event)"
  /></app-theme-container>`,
  imports: [ThemeContainerComponent, TrackingComponent, AsyncPipe],
})
export class TrackingContainerComponent implements OnInit {
  private store = inject<Store<AppState>>(Store)
  private preferenceServiceDialog = inject(NewPreferenceDialogService)
  private _snackBar = inject(MatSnackBar)

  trackingData$: Observable<TrackingData[] | null>
  trackingMyViewsData$: Observable<TrackingViewsData[] | null>
  trackingTeamViewsData$: Observable<TrackingViewsData[] | null>
  trackingClientViewsData$: Observable<TrackingViewsData[] | null>

  selectors$: Observable<TrackingSelectors | null>
  inceptionInterval$: Observable<BasicControl>
  isLoading$: Observable<boolean>
  isDetailsLoading$: Observable<boolean>
  userPreferences$: Observable<SelectedUserPreferences[] | null>
  users$: ApiResponse<User[]>
  myViewSelectors$: Observable<TrackingSelectors | null>
  teamViewSelectors$: Observable<TrackingSelectors | null>

  private backendService = inject(BackendService)

  ngOnInit() {
    this.trackingData$ = this.store.pipe(select(selectTrackingData))
    this.trackingMyViewsData$ = this.store.pipe(select(selectTrackingMyViewsData))
    this.trackingTeamViewsData$ = this.store.pipe(select(selectTrackingTeamViewsData))
    this.trackingClientViewsData$ = this.store.pipe(select(selectTrackingClientViewsData))
    this.selectors$ = this.store.pipe(select(selectTrackingSelectors))
    this.myViewSelectors$ = this.store.pipe(select(selectMyViewTrackingSelectors))
    this.teamViewSelectors$ = this.store.pipe(select(selectTeamViewTrackingSelectors))

    this.inceptionInterval$ = this.store.pipe(select(selectInceptionTimeInterval))
    this.isLoading$ = this.store.pipe(select(selectIsLoading))
    this.isDetailsLoading$ = this.store.pipe(select(selectIsDetailsLoading))
    this.userPreferences$ = this.store.pipe(select(selectTrackingUserPreferences))
    // this.users$ = this.backendService.getAllUsers()
    this.users$ = this.backendService.getUsersWithSalesforceAccount()

    this.store.dispatch(fetchTracking())
    this.store.dispatch(fetchMyViewTracking({ applyDefaultYear: true }))
    this.store.dispatch(fetchTeamViewTracking({ applyDefaultYear: true }))
  }

  onFiltersChanged(props: { filters: Record<string, string[]> }): void {
    this.store.dispatch(
      filtersChanged({
        filters: props.filters,
      }),
    )
  }

  onFiltersChangeMyView(props: { filters: Record<string, string[]> }): void {
    this.store.dispatch(
      filtersChangedMyView({
        filters: props.filters,
      }),
    )
  }
  onFiltersChangeTeamView(props: { filters: Record<string, string[]> }): void {
    this.store.dispatch(
      filtersChangedTeamView({
        filters: props.filters,
      }),
    )
  }

  onFetchTrackingDetailsData(programId: number): void {
    this.store.dispatch(
      fetchTrackingDetails({
        programId,
      }),
    )
  }

  onInceptionDateChanged($event: { minInceptionDate: string; maxInceptionDate: string }): void {
    this.store.dispatch(inceptionDateChanged($event))
  }

  onUpdateComplete(programId: number): void {
    this.store.dispatch(updateCompleteProgram({ programId }))
  }
  onUpdateColumnValue($event: {
    opportunityId: number
    category: string
    newValue: string
    dateTime: string
    viewType: string
  }): void {
    this.store.dispatch(
      updateColumnValue({
        opportunityId: $event.opportunityId,
        category: $event.category,
        newValue: $event.newValue,
        date: $event.dateTime,
        viewType: $event.viewType,
      }),
    )
  }

  onUpdateUserValue($event: {
    opportunityId: number
    email_id: string[]
    OpportunityTeamRoles: string
    viewType: string
  }): void {
    this.store.dispatch(
      updateUserValue({
        opportunityId: $event.opportunityId,
        role: $event.OpportunityTeamRoles,
        email_ids: $event.email_id,
        viewType: $event.viewType,
      }),
    )
  }
  onUpdateComment($event: {
    opportunityId: number
    category: string
    comment: string
    viewType: string
  }): void {
    this.store.dispatch(
      updateComment({
        opportunityId: $event.opportunityId,
        category: $event.category,
        comment: $event.comment,
        viewType: $event.viewType,
      }),
    )
  }

  async onSavePreference($event?: { filters?: Record<string, string[]> }): Promise<void> {
    // Get preferences once from the store
    const data = await firstValueFrom(this.store.pipe(select(selectTrackingUserPreferences)))

    if (data && Array.isArray(data)) {
      // Open the dialog
      const dialogRef = this.preferenceServiceDialog.open('')
      const label = await firstValueFrom(dialogRef.afterClosed())
      if (!label) return
      // Check duplicate label
      const exists = data.some((item) => {
        try {
          const pref = JSON.parse(item.preferenceValue)
          return pref.label?.trim().toLowerCase() === label.trim().toLowerCase()
        } catch {
          return false
        }
      })

      if (exists) {
        this._snackBar.open('This view name already exists. Please use a different one.', 'X', {
          duration: 2000,
        })

        return
      }

      this.store.dispatch(saveTrackingPreferences({ label, filters: $event?.filters }))
      dialogRef.close()
    }
  }

  setTrackingPreferences($event: { id: number }): void {
    this.store.dispatch(setSelectedUserPreferences({ id: $event.id }))
  }

  deleteTrackingPreferences($event: { deletedPreferences: SelectedUserPreferences }): void {
    this.store.dispatch(deleteUserPreference({ deletedPreferences: $event.deletedPreferences }))
  }

  updateTrackingPreferences($event: { updatePreferences: SelectedUserPreferences }): void {
    this.store.dispatch(updateUserPreference({ updatedPreferences: $event.updatePreferences }))
  }
}
